"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeDraw = void 0;
var tslib_1 = require("tslib");
var core_api_1 = require("@ikegps/core-api");
var mobx_1 = require("mobx");
var mobx_utils_1 = require("mobx-utils");
var id_1 = require("../id");
var model_1 = require("../model");
var draw_state_1 = require("./draw.state");
var draw_util_1 = require("./draw.util");
var svg_event_1 = require("./draw/svg/svg.event");
var svg_wheel_1 = require("./draw/svg/svg.wheel");
var IkeDraw = /** @class */ (function () {
    function IkeDraw(ctx) {
        var _this = this;
        var _a;
        this.id = 'ds_' + id_1.IkeId.generate();
        this.isMarkable = false;
        /** List of images already loaded by the browser */
        this.imageMap = mobx_1.observable.map(null, { deep: false, name: "Draw-ImageLoaded-".concat(this.id) });
        this._lastUnits = null;
        this._getLabelText = null;
        this._getBrightness = null;
        this._getCrop = null;
        this.collection = ctx.collection;
        this.capture = ctx.capture;
        this.drawState = this.collection.getDrawableState(ctx.capture.objectId);
        this.brightnessCorrectionState = this.collection.getBrightnessCorrectionState(ctx.capture.objectId);
        this.imageVisibilityState = this.collection.getImageVisibilityState(ctx.capture.objectId);
        this.cropParameterState = this.collection.getCropParameterState(ctx.capture.objectId);
        this.state = new draw_state_1.IkeDrawState(this);
        this.window = (_a = ctx.window) !== null && _a !== void 0 ? _a : null;
        if (ctx.captureMaskFetcher) {
            this.captureMaskFetcher = ctx.captureMaskFetcher;
        }
        this.orientation = this.capture.resolution.width > this.capture.resolution.height ? 'landscape' : 'portrait';
        this.init();
        this.boundWheelEvent = function (el) { return svg_wheel_1.SVGWheel.onWheel(_this, el); };
        this.boundEvents = new svg_event_1.SVGEventHandler(this, ctx.onStartHOASession);
    }
    /** Use a transformer to cache the drawableId to text conversions they dont change very often. */
    IkeDraw.prototype.getLabelText = function (drawableId, options) {
        var _this = this;
        var _a, _b;
        var units = options.units;
        if (this._lastUnits !== units) {
            this._lastUnits = units;
            this._getLabelText = function (drawId, ctx) {
                var drawable = _this.drawState.getDrawable(drawId);
                if (model_1.CollectionModelDrawableMarker.isMarker(drawable)) {
                    return draw_util_1.IkeDrawUtil.getMarkerLabelText(_this.capture, drawable, ctx);
                }
                if (model_1.CollectionModelDrawableLine.isLine(drawable)) {
                    return draw_util_1.IkeDrawUtil.getLineLabelText(drawable, ctx);
                }
                return [];
            };
        }
        return (_b = (_a = this._getLabelText) === null || _a === void 0 ? void 0 : _a.call(this, drawableId, options)) !== null && _b !== void 0 ? _b : [];
    };
    /**
     * Returns an array of the first 4 colors of spans if the Marker belongs to Span
     * @param drawableId formID from marker
     */
    IkeDraw.prototype.getSpanColors = function (drawableId) {
        var drawable = this.drawState.getDrawable(drawableId);
        var label = drawable.label;
        var labelLinks = label.links;
        var colors = [];
        for (var _i = 0, labelLinks_1 = labelLinks; _i < labelLinks_1.length; _i++) {
            var link = labelLinks_1[_i];
            var color = model_1.FormModelFieldSubForm.getSpanColor(link.parent);
            if (color && colors.indexOf(color) === -1) {
                colors.push(color);
            }
        }
        return colors ? colors.slice(0, 4) : null;
    };
    IkeDraw.prototype.getBrightness = function (brightnessCorrectionId) {
        var _this = this;
        this._getBrightness = (0, mobx_utils_1.createTransformer)(function (brightnessId) {
            var brightnessCorrection = _this.brightnessCorrectionState.getBrightnessCorrection(brightnessId);
            if (model_1.CollectionModelGammaBrightnessCorrection.isGamma(brightnessCorrection)) {
                return brightnessCorrection.exponent;
            }
            return 1;
        });
        return this._getBrightness(brightnessCorrectionId);
    };
    IkeDraw.prototype.getCrop = function (cropParameterId) {
        var _this = this;
        this._getCrop[0] = (0, mobx_utils_1.createTransformer)(function (cropId) {
            var cropParameter = _this.cropParameterState.getCropParameter(cropId);
            if (model_1.CollectionModelFreeCropParameter.isFree(cropParameter)) {
                return cropParameter;
            }
            return 1;
        });
        return this._getCrop(cropParameterId);
    };
    IkeDraw.prototype.setImageLoaded = function (imageId, loaded) {
        if (loaded === void 0) { loaded = true; }
        if (this.imageMap.has(imageId)) {
            return;
        }
        this.imageMap.set(imageId, loaded);
    };
    /** Pre load the image into the browser cache */
    IkeDraw.prototype.loadImage = function (imageId, url) {
        var _this = this;
        if (this.imageMap.has(imageId)) {
            return;
        }
        var image = new Image();
        image.onload = function () { return _this.setImageLoaded(imageId); };
        image.onerror = function () { return _this.setImageLoaded(imageId, false); };
        image.src = url;
    };
    IkeDraw.prototype.isImageReady = function (imageId) {
        return this.imageMap.get(imageId) === true;
    };
    IkeDraw.prototype.init = function () {
        if (this.capture != null) {
            if (this.state.cropHeight > 0 && this.state.cropWidth > 0 && !this.isMarkable) {
                this.state.setCenter(this.state.cropX + (this.state.cropWidth * 0.5), this.state.cropY + (this.state.cropHeight * 0.5));
            }
            else {
                this.state.setCenter(this.capture.resolution.width / 2, this.capture.resolution.height / 2);
            }
        }
        this.state.selectedId = null;
        this.state.hoverId = null;
        this.state.setTool(null);
        this.state.setCrosshair(false);
        if (this.window) {
            this.state.isDateLabelVisible = false;
            this.state.isBearingLabelVisible = false;
        }
        else {
            this.state.isDateLabelVisible = true;
            this.state.isBearingLabelVisible = true;
        }
        if (!this.isIkePhoto) {
            this.state.setCrosshair(true);
        }
        else if (!this.drawState.hasUserMeasurements()) {
            this.state.setCrosshair(true);
        }
    };
    Object.defineProperty(IkeDraw.prototype, "geometry3d", {
        get: function () {
            return this.collection.state.getGeometry(this.capture.objectId);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDraw.prototype, "isIkePhoto", {
        /** Does this draw state have enough information for photo measure */
        get: function () {
            return this.capture.type === core_api_1.IkeCaptureType.TRUE_SIZE;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDraw.prototype, "isMoving", {
        /** are any elements on the page currently moving */
        get: function () {
            return this.state.moveId != null;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDraw.prototype, "isLinking", {
        get: function () {
            return this.state.linkFieldIds.length > 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDraw.prototype, "selected", {
        get: function () {
            if (this.state.selectedId == null) {
                return null;
            }
            if (this.state.selectedId === 'image') {
                return null;
            }
            if (!this.drawState.hasDrawable(this.state.selectedId)) {
                return null;
            }
            return this.drawState.getDrawable(this.state.selectedId);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDraw.prototype, "highlighted", {
        get: function () {
            if (this.state.hoverId == null) {
                return null;
            }
            if (this.state.hoverId === 'image') {
                return null;
            }
            if (!this.drawState.hasDrawable(this.state.hoverId)) {
                return null;
            }
            return this.drawState.getDrawable(this.state.hoverId);
        },
        enumerable: false,
        configurable: true
    });
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDraw.prototype, "isMarkable", void 0);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDraw.prototype, "setImageLoaded", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDraw.prototype, "init", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDraw.prototype, "isIkePhoto", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDraw.prototype, "isMoving", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDraw.prototype, "isLinking", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDraw.prototype, "selected", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDraw.prototype, "highlighted", null);
    return IkeDraw;
}());
exports.IkeDraw = IkeDraw;
//# sourceMappingURL=draw.js.map